import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useState } from "react";

import {
  Badge,
  Button,
  ButtonOwnProps,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import PendingIcon from "@mui/icons-material/Pending";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import { visuallyHidden } from "@mui/utils";
import {
  Box,
  TableContainer,
  TablePagination,
  TableSortLabel,
} from "@mui/material";

import conf from "config-dlm";
import {
  useUserServiceProjects,
  useUserServiceProjectsAssign,
  useUserServiceProjectsUnassign,
} from "hooks/user/projects";
import InfoChip from "components/utils/InfoChip";
import { User } from "components/auth/AuthProvider";
import ErrorChip from "components/utils/ErrorChip";
import SuccessChip from "components/utils/SuccessChip";
import usePagination from "hooks/pagination";
import { ProjectSchema } from "hooks/pavm/base";
import LinearIndeterminate from "components/utils/LinearLoader";
import {
  ActionToggleGroup,
  ActionType,
  AccessToggleGroup,
  RoleToggleGroup,
} from "./Radio";

const ProjectRow = (props: {
  user: User;
  action: ActionType;
  project: ProjectSchema;
  handleClick: (e: React.MouseEvent<unknown>, id: string) => void;
  isItemSelected: boolean;
}) => {
  const { action, user, project, handleClick, isItemSelected } = props;
  const {
    id,
    name,
    tags,
    user: { lastActive },
  } = project;

  const {
    project: { assigned },
  } = user;

  const connected = project.user.connected;
  const connectedUser = project.user.email;

  const isAssigned = assigned?.some((id) => id === project.id);

  const isDisabled =
    (action === "assign" && isAssigned) ||
    (action === "unassign" && !isAssigned);

  return (
    <TableRow
      key={id}
      tabIndex={-1}
      selected={isItemSelected}
      aria-checked={isItemSelected}
      sx={conf.LAST_CHILD_NO_BORDER}
    >
      <TableCell padding="checkbox">
        <Checkbox
          color="primary"
          checked={isItemSelected}
          onClick={(e) => handleClick(e, id)}
          disabled={isDisabled}
          inputProps={{ "aria-labelledby": id }}
        />
      </TableCell>

      <TableCell component="th" scope="row">
        {name}
      </TableCell>

      <TableCell align="right">
        <InfoChip size="small" color="primary" info={tags.join(", ")} />
      </TableCell>

      <TableCell align="right">
        {lastActive ? moment(lastActive).fromNow() : "--"}
      </TableCell>

      <TableCell align="right">
        {connected ? <b>{connectedUser}</b> : "--"}
      </TableCell>

      <TableCell align="right">
        <Chip
          color={isAssigned ? "success" : "primary"}
          label={isAssigned ? "Yes" : "No"}
        />
      </TableCell>
    </TableRow>
  );
};

type ProjectsProps = {
  user: User;
  action: ActionType;
  selected: readonly string[];
  setSelected: Dispatch<SetStateAction<readonly string[]>>;
  handleClick: (e: React.MouseEvent<unknown>, id: string) => void;
};

const Projects = (props: ProjectsProps) => {
  const { user, action, selected, setSelected, handleClick } = props;

  const paginate = usePagination({ defaultSort: "email" });
  const { order, sort, page, limit, body } = paginate;
  const { handleChangeLimit, handleChangePage, handleSort } = paginate;

  const projects = useUserServiceProjects(body);

  // const totalProjects = projects?.data?.data.length ?? 0;
  // const totalSelected = selected.length;

  // const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   if (event.target.checked) {
  //     const newSelected =
  //       projects?.data?.data?.map((project) => project.id) ?? [];
  //     setSelected(newSelected);
  //     return;
  //   }

  //   setSelected([]);
  // };

  return (
    <TableContainer sx={{ boxShadow: 4, my: 2 }}>
      <Table aria-label="collapsible table">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              {/* <Checkbox
                color="primary"
                onChange={handleSelectAllClick}
                checked={
                  projects?.data?.data &&
                  projects?.data?.data.length > 0 &&
                  selected.length === projects?.data?.data.length
                }
                indeterminate={
                  totalSelected > 0 && totalSelected < totalProjects
                }
                inputProps={{ "aria-label": "select all users" }}
              /> */}
            </TableCell>

            {conf.COLUMNS.PROJECTS.MANAGE.map((column) => (
              <TableCell
                key={column.label}
                sortDirection={order}
                align={column.isHeader ? "left" : "right"}
              >
                <TableSortLabel
                  active={sort === column.key}
                  direction={sort === column.key ? order : "asc"}
                  onClick={(event: React.MouseEvent<unknown>) =>
                    handleSort(event, column.key)
                  }
                >
                  <b>{column.label}</b>
                  {sort === column.key ? (
                    <Box component="span" sx={visuallyHidden}>
                      {order === "desc"
                        ? "sorted descending"
                        : "sorted ascending"}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>

        {projects.isLoading ? (
          <TableBody>
            <TableRow>
              <TableCell colSpan={8}>
                <LinearIndeterminate />
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {projects?.data?.data?.map((project: ProjectSchema) => {
              const isItemSelected = selected.includes(project.id);

              return (
                <ProjectRow
                  key={project.id}
                  user={user}
                  action={action}
                  project={project}
                  handleClick={handleClick}
                  isItemSelected={isItemSelected}
                />
              );
            })}
          </TableBody>
        )}
      </Table>

      <TablePagination
        component="div"
        rowsPerPageOptions={[10, 25, 50, 100]}
        count={projects?.data?.meta.total || 0}
        page={page}
        rowsPerPage={limit}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeLimit}
      />
    </TableContainer>
  );
};

type ManageViewProps = {
  user: User;
  open: boolean;
  onClose: () => void;
};

const ManageView = (props: ManageViewProps) => {
  const { open, user, onClose } = props;

  const [note, setNote] = useState<string>("");
  const [action, setAction] = useState<ActionType>("assign");
  const [selected, setSelected] = useState<readonly string[]>([]);

  const assign = useUserServiceProjectsAssign();
  const unassign = useUserServiceProjectsUnassign();

  const handleClick = (e: React.MouseEvent<unknown>, id: string) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleSubmit = () => {
    if (action === "assign") {
      assign.mutate({ userId: user.id, projectIds: selected, note });
    } else if (action === "unassign") {
      unassign.mutate({ userId: user.id, projectIds: selected, note });
    }
  };

  useEffect(() => {
    setSelected([]);
  }, [action]);

  useEffect(() => {
    if (assign.isSuccess) {
      setNote("");
      setSelected([]);
    }
  }, [assign.isSuccess]);

  useEffect(() => {
    if (unassign.isSuccess) {
      setNote("");
      setSelected([]);
    }
  }, [unassign.isSuccess]);

  return (
    <Dialog fullWidth maxWidth="lg" onClose={onClose} open={open}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <DialogTitle>
          <Chip
            color="success"
            icon={<AssignmentIndIcon />}
            label={<b>{user.email}</b>}
          />
        </DialogTitle>

        <Button size="small" color="inherit" onClick={onClose}>
          <CloseIcon />
        </Button>
      </Stack>

      <Divider />

      <DialogContent>
        <Typography gutterBottom variant="h6" component="div">
          Role
        </Typography>

        <DialogContentText variant="caption">
          To manage, click Edit then select new user role. Changes will not take
          effect until submitted & confirmed. Note: For security, cannot modify
          or change to 'admin' role.
        </DialogContentText>

        <RoleToggleGroup user={user} />

        <Divider sx={{ mb: 2 }} />

        <Typography gutterBottom variant="h6" component="div">
          Projects
        </Typography>

        <Stack
          direction={"row"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <DialogContentText variant="caption">
            To manage user projects please chose action type (right), then
            select project(s) below.
          </DialogContentText>

          <ActionToggleGroup action={action} setAction={setAction} />
        </Stack>

        <Projects
          user={user}
          action={action}
          selected={selected}
          setSelected={setSelected}
          handleClick={handleClick}
        />

        <TextField
          fullWidth
          id="note"
          type="note"
          label="Note (optional)"
          margin="dense"
          variant="standard"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            onClick={handleSubmit}
            disabled={selected.length === 0}
          >
            Submit
          </Button>
        </DialogActions>

        <Divider sx={{ mb: 2 }} />

        <Stack direction={"row"} justifyContent={"space-between"}>
          <Typography gutterBottom variant="h6" component="div">
            Account status
          </Typography>
          {user.status === "active" ? (
            <SuccessChip
              info={"This account is currently active"}
              label={"active"}
              placement="left"
            />
          ) : (
            <ErrorChip
              info={"This account is currently: " + user.status.toUpperCase()}
              icon={user.status === "pending" ? <PendingIcon /> : <BlockIcon />}
              label={user.status}
              color={user.status === "pending" ? "warning" : "error"}
              placement="left"
            />
          )}
        </Stack>

        <DialogContentText variant="caption">
          To modify account access, select new status then submit. Changes will
          not take effect until submitted & confirmed. Note: Will ask for
          confirmation.
        </DialogContentText>

        <AccessToggleGroup user={user} />
      </DialogContent>
    </Dialog>
  );
};

const ManageDialog = ({
  user,
  size = "medium",
  disabled = false,
  fullWidth = false,
}: {
  user: User;
  size?: ButtonOwnProps["size"];
  detail?: any;
  disabled?: boolean;
  fullWidth?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const Manage = (
    <Button
      size={size}
      type="submit"
      color="primary"
      variant="contained"
      onClick={handleOpen}
      disabled={disabled}
      fullWidth={fullWidth}
    >
      Manage
    </Button>
  );

  return (
    <div>
      {user.status === "pending" ? (
        <Tooltip title="Pending account activation" placement="left">
          <Badge color="error" variant="dot">
            {Manage}
          </Badge>
        </Tooltip>
      ) : (
        Manage
      )}

      <ManageView open={open} user={user} onClose={handleClose} />
    </div>
  );
};

export default ManageDialog;
